import { useState } from 'react';

import {
  IconAlertOctagonFilled,
  IconChevronDown,
  IconChevronUp,
  IconTrash,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import type { CustomQuizQuestion } from '@cfa/pathway-fe-shared/xp';
import {
  Card,
  CardContent,
  Dropdown,
  TextField,
  useBreakpoints,
  useMediaQuery,
} from '@cfa/react-components';

import useAuth from '@/hooks/useAuth';

import { cn } from '@/utils/tailwind';

import ConfirmationModal from 'components/popups/ConfirmationModal';
import Constants from 'constants/index';
import { validateTextField } from 'utils/validateTextField';

import BuildQuizzesAnswers, { BuildQuizzesProps } from './BuildQuizzesAnswers';
import CollapsedQuestionPreview from './CollapsedQuestionPreview';

import { TranslationKey } from '@/i18n';
import SixDotIcon from '@/routes/TrainingPlans/BuildPlans/EditablePlanItems/SixDotIcon';

const questionTemplateTypesArray = Object.values(
  Constants.QUESTION_TEMPLATE_TYPES,
);

interface QuestionTemplateType {
  id: string;
  templateName: TranslationKey;
  isMultipleChoiceTemplate: boolean;
}

/** Resolves the question template for the dropdown from `questionType`
 *  Legacy Quizzes: infers T/F vs multiple choice from answer count */
const getQuestionTemplateType = (question: CustomQuizQuestion | undefined) => {
  if (!question) {
    return Constants.QUESTION_TEMPLATE_TYPES.MULTIPLE_CHOICE;
  }

  if (
    question.questionType === Constants.QUESTION_TEMPLATE_TYPES.TRUE_OR_FALSE.id
  ) {
    return Constants.QUESTION_TEMPLATE_TYPES.TRUE_OR_FALSE;
  }

  if (
    question.questionType ===
    Constants.QUESTION_TEMPLATE_TYPES.MULTIPLE_CHOICE.id
  ) {
    return Constants.QUESTION_TEMPLATE_TYPES.MULTIPLE_CHOICE;
  }

  return question.answers.length === 2
    ? Constants.QUESTION_TEMPLATE_TYPES.TRUE_OR_FALSE
    : Constants.QUESTION_TEMPLATE_TYPES.MULTIPLE_CHOICE;
};

interface BuildQuizzesQuestionCardProps extends BuildQuizzesProps {
  handleRef?: React.Ref<HTMLElement>;
  isDragSource?: boolean;
  isCollapsed?: boolean;
  isAnyDragActive?: boolean;
  isDropTarget?: boolean;
}

const BuildQuizzesQuestionCard: React.FC<BuildQuizzesQuestionCardProps> = ({
  handlePassingGrade,
  handleRef,
  isDragSource = false,
  isCollapsed = false,
  isAnyDragActive = false,
  isDropTarget = false,
  onDeleteQuestion,
  questionIndex,
  quiz,
  setQuiz,
  setValidationMode,
  validationMode,
  moveQuestionDown,
  moveQuestionUp,
  totalQuestions,
}) => {
  const { t } = useTranslation();
  const { data: auth } = useAuth();
  const userLanguage = auth?.language ?? 'en';
  const breakpoints = useBreakpoints();
  // Mobile and desktop each render their own grip. A React ref can only point
  // at one node, so only mount the grip for the active breakpoint with handleRef.
  const isMdAndUp = useMediaQuery(breakpoints.up('md'));

  const [showDeleteQuestionPopup, setShowDeleteQuestionPopup] = useState(false);
  const [isReorderAnimating, setIsReorderAnimating] = useState(false);

  const handleMoveUp = () => {
    setIsReorderAnimating(true);
    moveQuestionUp?.(questionIndex);
    setTimeout(() => setIsReorderAnimating(false), 200);
  };

  const handleMoveDown = () => {
    setIsReorderAnimating(true);
    moveQuestionDown?.(questionIndex);
    setTimeout(() => setIsReorderAnimating(false), 200);
  };

  const questionTemplateType = getQuestionTemplateType(
    quiz.questions[questionIndex],
  );
  const correctOptionSelected =
    quiz.questions[questionIndex]?.answers.some(
      option => option.correct === true,
    ) ?? false;

  const questionNumber = questionIndex + 1;
  const hasMultipleQuestions = (totalQuestions ?? 0) > 1;
  const isLastQuestion = questionIndex === (totalQuestions ?? 0) - 1;

  const handleQuestionName = (value: string) => {
    setQuiz(prevState => ({
      ...prevState,
      questions: prevState.questions.map((question, qi) =>
        qi === questionIndex
          ? {
              ...question,
              question: { ...question.question, [userLanguage]: value },
            }
          : question,
      ),
    }));
  };

  const handleTemplateChange = (value: QuestionTemplateType | null) => {
    if (!value || value.id === questionTemplateType.id) return;
    setValidationMode(false);
    const newTemplateAnswers =
      value.id === Constants.QUESTION_TEMPLATE_TYPES.MULTIPLE_CHOICE.id
        ? Constants.DEFAULT_MULTIPLE_CHOICE_QUESTION
        : Constants.DEFAULT_TRUE_FALSE_QUESTION;
    setQuiz(prevState => ({
      ...prevState,
      questions: prevState.questions.map((question, qi) =>
        qi === questionIndex
          ? {
              ...question,
              questionType: value.id,
              answers: newTemplateAnswers.answers,
            }
          : question,
      ),
    }));
  };

  const deleteQuestion = () => {
    handlePassingGrade?.();
    // Note: No need to manually save to storage here - the parent component's
    // useEffect in BuildQuizzesView automatically saves quiz state to the atom
    // when it changes via setQuiz
    setQuiz(prevState => ({
      ...prevState,
      questions: prevState.questions.filter(
        (_, index) => index !== questionIndex,
      ),
    }));
    onDeleteQuestion?.();
    setShowDeleteQuestionPopup(false);
  };

  const question = quiz.questions[questionIndex];
  const isCollapsedState = isAnyDragActive || isCollapsed;
  const hasError =
    validationMode &&
    (!validateTextField(question?.question[userLanguage] ?? '') ||
      !correctOptionSelected ||
      (question?.answers.some(
        a => !validateTextField(a.answer[userLanguage] ?? ''),
      ) ??
        false));
  return (
    <>
      <div>
        {question && (
          <div
            className={cn(
              'grid transition-[grid-template-rows] duration-300 ease-in-out',
              isCollapsedState ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
            )}
          >
            <div className="min-h-0 overflow-hidden">
              {isDragSource ? (
                <div className="mb-4 flex h-6 items-center">
                  <div className="bg-secondary-base h-2 w-full rounded-full" />
                </div>
              ) : (
                <CollapsedQuestionPreview
                  question={question}
                  questionIndex={questionIndex}
                  hasError={hasError}
                />
              )}
            </div>
          </div>
        )}
        <div
          className={cn(
            'grid transition-[grid-template-rows] duration-300 ease-in-out',
            isCollapsedState ? 'grid-rows-[0fr]' : 'grid-rows-[1fr]',
          )}
        >
          <div
            className={cn(
              'min-h-0 overflow-clip',
              !isCollapsedState && '[overflow-clip-margin:6px]',
            )}
          >
            <Card
              elevation={1}
              variant="outlined"
              style={{
                transition:
                  'background-color 750ms, color 750ms, border-color 750ms, box-shadow 300ms',
              }}
              className={cn(
                'group mb-4 overflow-hidden',
                isReorderAnimating && '[box-shadow:none]!',
                isDropTarget &&
                  'bg-secondary-base! [&_*:not(.cfa-dropdown,.cfa-dropdown_*,input,textarea)]:text-white!',
                hasError && 'border-feedback-error-base! border-2!',
              )}
            >
              <CardContent>
                <div className="flex items-center justify-between md:hidden">
                  <div className="flex items-center">
                    {handleRef && !isMdAndUp && (
                      <span className="pr-3">
                        <SixDotIcon position="middle" ref={handleRef} />
                      </span>
                    )}
                    <div className="text-grayscale-gray6 relative mt-1 flex shrink-0 items-center self-start">
                      <div className="text-grayscale-gray7 bg-outline-2 mr-3 flex size-10 items-center justify-center rounded-full text-lg font-bold">
                        {questionNumber}
                      </div>
                      {hasMultipleQuestions &&
                        questionIndex > 0 &&
                        !isAnyDragActive && (
                          <button
                            onClick={handleMoveUp}
                            className="flex cursor-pointer items-center justify-center p-0"
                            type="button"
                            aria-label={`Move question ${questionNumber} up`}
                          >
                            <IconChevronUp size={30} stroke={1} />
                          </button>
                        )}

                      {hasMultipleQuestions &&
                        !isLastQuestion &&
                        !isAnyDragActive && (
                          <button
                            onClick={handleMoveDown}
                            className="flex cursor-pointer items-center justify-center p-0"
                            type="button"
                            aria-label={`Move question ${questionNumber} down`}
                          >
                            <IconChevronDown size={30} stroke={1} />
                          </button>
                        )}
                    </div>
                  </div>
                  {hasMultipleQuestions && (
                    <button
                      type="button"
                      aria-label={`Delete question ${questionNumber}`}
                      onClick={() => setShowDeleteQuestionPopup(true)}
                      className="text-grayscale-gray6 cursor-pointer transition-opacity group-focus-within:pointer-events-auto group-focus-within:opacity-100 group-hover:pointer-events-auto group-hover:opacity-100 [@media(hover:hover)]:pointer-events-none [@media(hover:hover)]:opacity-0"
                    >
                      <IconTrash size={24} />
                    </button>
                  )}
                </div>

                <div className="mt-8 flex w-full items-start gap-5 md:gap-4">
                  {handleRef && isMdAndUp && (
                    <div
                      className={cn(
                        '-mt-6',
                        !hasMultipleQuestions &&
                          'pointer-events-none opacity-0',
                      )}
                    >
                      <SixDotIcon position="top" ref={handleRef} />
                    </div>
                  )}
                  <div className="text-grayscale-gray6 relative mt-1 hidden items-center md:flex">
                    {hasMultipleQuestions &&
                      questionIndex > 0 &&
                      !isAnyDragActive && (
                        <button
                          onClick={handleMoveUp}
                          className="absolute top-0 left-1/2 flex size-5 -translate-x-1/2 -translate-y-[120%] cursor-pointer items-center justify-center p-0 transition-opacity group-focus-within:pointer-events-auto group-focus-within:opacity-100 group-hover:pointer-events-auto group-hover:opacity-100 [@media(hover:hover)]:pointer-events-none [@media(hover:hover)]:opacity-0"
                          type="button"
                          aria-label={`Move question ${questionNumber} up`}
                        >
                          <IconChevronUp size={20} stroke={2} />
                        </button>
                      )}
                    <div className="text-grayscale-gray7 bg-outline-2 flex size-10 items-center justify-center rounded-full text-lg font-bold">
                      {questionNumber}
                    </div>
                    {hasMultipleQuestions &&
                      !isLastQuestion &&
                      !isAnyDragActive && (
                        <button
                          onClick={handleMoveDown}
                          className="absolute bottom-0 left-1/2 flex size-5 -translate-x-1/2 translate-y-[120%] cursor-pointer items-center justify-center p-0 transition-opacity group-focus-within:pointer-events-auto group-focus-within:opacity-100 group-hover:pointer-events-auto group-hover:opacity-100 [@media(hover:hover)]:pointer-events-none [@media(hover:hover)]:opacity-0"
                          type="button"
                          aria-label={`Move question ${questionNumber} down`}
                        >
                          <IconChevronDown size={20} stroke={2} />
                        </button>
                      )}
                  </div>
                  <div className="flex min-w-0 flex-1 flex-col-reverse gap-2 lg:flex-row">
                    <div className="w-full lg:max-w-[250px] lg:min-w-[175px] lg:flex-1 lg:shrink-0">
                      <Dropdown
                        data-testid={t(
                          questionTemplateType.templateName,
                        ).replace(/\s/g, '')}
                        className="w-full! max-w-none! min-w-[100px]!"
                        getOptionId={option => option.id}
                        getOptionText={option => t(option.templateName)}
                        onChange={newVal => handleTemplateChange(newVal)}
                        options={questionTemplateTypesArray}
                        renderOption={option => t(option.templateName)}
                        value={questionTemplateType}
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <TextField
                        className="w-full! max-w-none! [&_.cfa-input-label]:sr-only! [&_input]:w-full! [&_input]:max-w-none!"
                        data-testid="EnterQuestion"
                        errorText={
                          !validateTextField(
                            question?.question[userLanguage] ?? '',
                          ) && validationMode
                            ? t('TrainingPlans.buildQuizzes.errorQuestion')
                            : ''
                        }
                        label={`${t('Generic.question')} ${questionNumber}`}
                        maxLength={250}
                        onChange={e => handleQuestionName(e.target.value)}
                        placeholder={t(
                          'TrainingPlans.buildQuizzes.enterQuestion',
                        )}
                        required
                        value={question?.question[userLanguage] ?? ''}
                      />
                    </div>
                  </div>
                  {hasMultipleQuestions && (
                    <div className="mt-2 hidden shrink-0 md:block">
                      <button
                        type="button"
                        aria-label={`Delete question ${questionNumber}`}
                        onClick={() => setShowDeleteQuestionPopup(true)}
                        className="text-grayscale-gray6 cursor-pointer transition-opacity group-focus-within:pointer-events-auto group-focus-within:opacity-100 group-hover:pointer-events-auto group-hover:opacity-100 [@media(hover:hover)]:pointer-events-none [@media(hover:hover)]:opacity-0"
                      >
                        <IconTrash size={24} />
                      </button>
                    </div>
                  )}
                </div>

                <div className="mt-4 text-left">
                  {questionTemplateType.isMultipleChoiceTemplate
                    ? t(
                        'TrainingPlans.buildQuizzes.buildQuestionMultipleChoice',
                      )
                    : t('TrainingPlans.buildQuizzes.buildQuestionTrueFalse')}
                  {!correctOptionSelected && validationMode && (
                    <div className="text-feedback-error-base mt-2 flex w-full items-center text-xs font-semibold">
                      <IconAlertOctagonFilled className="mr-2 size-4" />
                      {t('TrainingPlans.buildQuizzes.errorSelectCorrectAnswer')}
                    </div>
                  )}
                </div>
                <BuildQuizzesAnswers
                  questionIndex={questionIndex}
                  questionTemplateType={questionTemplateType}
                  quiz={quiz}
                  setQuiz={setQuiz}
                  setValidationMode={setValidationMode}
                  validationMode={validationMode}
                />
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      <ConfirmationModal
        bodyText={t('Generic.deleteUnnamedObject', {
          object: t('Generic.question').toLowerCase(),
        })}
        headerText={t('Generic.deleteHeader', {
          type: t('Generic.question'),
        })}
        isOpen={showDeleteQuestionPopup}
        onClose={() => setShowDeleteQuestionPopup(false)}
        primaryButtonColor={'primary'}
        primaryButtonHandler={deleteQuestion}
        primaryButtonText={t('Button.delete')}
        primaryButtonVariant="destructive"
        secondaryButtonHandler={() => setShowDeleteQuestionPopup(false)}
        secondaryButtonText={t('Button.cancel')}
      />
    </>
  );
};

export default BuildQuizzesQuestionCard;
